import Logger from '../lib/logger';

/**
 * Consent-gated tracking for the Finsweet Cookie Consent banner
 * (partials/cookie-consent.php) — ported from the Webflow build.
 *
 * GTM, GA4 and Clarity are only injected once analytics consent is
 * granted: right after "Accept all" / "Save settings", or on page load
 * when Finsweet's `fs-cc` cookie already records it. IDs come from the
 * wrapper's data attributes (see inc/cookie-consent.php).
 *
 * Also wires the footer "Your Privacy Choices" link
 * ([data-cookie-preferences]) to reopen the banner.
 */
class CookieConsent {
  constructor(dom) {
    this.NAME = 'CookieConsent';
    this.dom = dom;
    this.banner = dom.querySelector('[fs-cc="banner"]');
    this.checkbox = dom.querySelector('[fs-cc-checkbox="analytics"]');
    this.loaded = false;
  }

  init() {
    this.dom.querySelectorAll('[fs-cc="allow"], [fs-cc="submit"]').forEach((button) => {
      // Defer so Finsweet has applied the new consents first.
      button.addEventListener('click', () => setTimeout(() => this.maybeLoad(), 0));
    });

    // Return visits: the cookie is readable immediately; the checkbox is
    // only synced once Finsweet initialises on window load.
    this.maybeLoad();
    window.addEventListener('load', () => setTimeout(() => this.maybeLoad(), 100));

    this.bindReopen();
  }

  maybeLoad() {
    if (this.hasConsent()) this.loadTracking();
  }

  hasConsent() {
    if (this.checkbox && this.checkbox.checked) return true;

    // Finsweet stores {id, consents: {analytics: bool, ...}} URI-encoded.
    const match = document.cookie.match(/(?:^|;\s*)fs-cc=([^;]*)/);
    if (!match) return false;

    try {
      const data = JSON.parse(decodeURIComponent(match[1]));
      return !!(data.consents && data.consents.analytics);
    } catch (e) {
      return false;
    }
  }

  loadTracking() {
    if (this.loaded) return;
    this.loaded = true;

    Logger.info(`${this.NAME} > loading GTM + GA4 + Clarity`);

    const { gtmId, ga4Id, clarityId } = this.dom.dataset;

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };

    if (gtmId) {
      window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      this.injectScript(`https://analytics.mediadesignschool.com/gtm.js?id=${encodeURIComponent(gtmId)}`);
    }

    if (ga4Id) {
      this.injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4Id)}`);
      window.gtag('js', new Date());
      window.gtag('config', ga4Id);
    }

    if (clarityId) {
      window.clarity = window.clarity || function clarity() {
        (window.clarity.q = window.clarity.q || []).push(arguments);
      };
      this.injectScript(`https://www.clarity.ms/tag/${encodeURIComponent(clarityId)}`);
    }
  }

  injectScript(src) {
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
  }

  // Showing the banner by hand (as the Webflow build does) sidesteps
  // Finsweet's own visibility state, so its close/allow/open-preferences
  // buttons may not hide it again — hide it explicitly on those.
  bindReopen() {
    if (!this.banner) return;

    document.querySelectorAll('[data-cookie-preferences]').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.banner.style.display = 'flex';
        this.banner.style.opacity = '1';
      });
    });

    this.banner.querySelectorAll('[fs-cc="close"], [fs-cc="allow"], [fs-cc="open-preferences"]').forEach((button) => {
      button.addEventListener('click', () => {
        this.banner.style.opacity = '0';
        this.banner.style.display = 'none';
      });
    });
  }
}

export default CookieConsent;
